import {
  clamp01,
  lerp,
  lerpRadians,
  lerpVector3,
} from "../core/math.ts";
import type { PublicTransportVehiclePresentation } from "../sim/publicTransport/index.ts";

/**
 * Detaches renderer-owned snapshots from the simulation's nested position
 * objects. The remaining presentation fields are immutable scalar/source data.
 */
export function copyPublicTransportPresentations(
  presentations: readonly Readonly<PublicTransportVehiclePresentation>[],
): readonly PublicTransportVehiclePresentation[] {
  return presentations.map((vehicle) => ({
    ...vehicle,
    position: { ...vehicle.position },
  }));
}

/**
 * Interpolates continuous vehicle motion between fixed simulation ticks.
 * Stop, door, route and occupancy fields deliberately use the current tick so
 * interaction state never lags behind what the simulation accepts.
 */
export function interpolatePublicTransportPresentations(
  previous: readonly Readonly<PublicTransportVehiclePresentation>[],
  current: readonly Readonly<PublicTransportVehiclePresentation>[],
  interpolationAlpha: number,
): readonly PublicTransportVehiclePresentation[] {
  const alpha = clamp01(interpolationAlpha);
  const previousById = new Map(
    previous.map((vehicle) => [vehicle.id, vehicle] as const),
  );

  return current.map((vehicle) => {
    const from = previousById.get(vehicle.id);
    if (from === undefined) {
      return {
        ...vehicle,
        position: { ...vehicle.position },
      };
    }
    return {
      ...vehicle,
      position: lerpVector3(from.position, vehicle.position, alpha),
      yawRadians: lerpRadians(from.yawRadians, vehicle.yawRadians, alpha),
      speedMps: lerp(from.speedMps, vehicle.speedMps, alpha),
      distanceAlongShapeM: lerp(
        from.distanceAlongShapeM,
        vehicle.distanceAlongShapeM,
        alpha,
      ),
    };
  });
}
